import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { API_URL } from "../config/api";
import {
  AlertTriangle,
  Building2,
  DollarSign,
  Edit,
  FileText,
  Plus,
  RefreshCw,
  Search,
  Upload,
  MoreVertical,
  CheckCircle,
  Clock,
  Ban,
  AlertOctagon,
  TrendingDown,
  CreditCard,
  Calendar,
  Flag,
  Landmark,
  Scale,
  Trash2,
  Archive,
  RotateCcw,
} from "lucide-react";


// Vertical colors
const VERTICAL_COLORS = {
  europacific: "bg-blue-100 text-blue-800 border-blue-300",
  artcom: "bg-purple-100 text-purple-800 border-purple-300",
  cargo_cfs: "bg-green-100 text-green-800 border-green-300",
  ep_holding: "bg-orange-100 text-orange-800 border-orange-300",
  other: "bg-slate-100 text-slate-800 border-slate-300",
};

// Urgency colors
const URGENCY_COLORS = {
  overdue: "bg-red-500 text-white",
  critical: "bg-orange-500 text-white",
  urgent: "bg-yellow-500 text-white",
  normal: "bg-green-100 text-green-700",
  unknown: "bg-slate-100 text-slate-600",
};

export default function IzvrsbePage() {
  const { token, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);

  // Data
  const [dashboard, setDashboard] = useState(null);
  const [izvrsbe, setIzvrsbe] = useState([]);
  const [urgentList, setUrgentList] = useState([]);
  const [verticals, setVerticals] = useState([]);
  
  // Filters
  const [selectedVertical, setSelectedVertical] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  // Confirmation dialogs
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Forms
  const [newIzvrsba, setNewIzvrsba] = useState({
    case_number: "",
    vertical: "europacific",
    creditor: "",
    principal_amount: 0,
    amount_to_pay: 0,
    amount_paid: 0,
    costs: 0,
    comment1: "",
    comment1_date: "",
    comment2: "",
    comment2_date: "",
    notes: "",
  });

  const [editForm, setEditForm] = useState({
    amount_to_pay: 0,
    amount_paid: 0,
    costs: 0,
    comment1: "",
    comment1_date: "",
    comment2: "",
    comment2_date: "",
    status: "active",
    notes: "",
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: "",
    notes: "",
  });

  // Fetch functions
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/izvrsbe/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboard(response.data);
      setVerticals(response.data.verticals || []);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    }
  }, [token]);

  const fetchIzvrsbe = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedVertical) params.append("vertical", selectedVertical);
      if (searchQuery) params.append("search", searchQuery);
      if (showArchived) params.append("status", "archived");

      const response = await axios.get(`${API_URL}/izvrsbe?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Filter archived items based on showArchived toggle
      const filteredData = showArchived 
        ? response.data.izvrsbe.filter(i => i.status === 'archived')
        : response.data.izvrsbe.filter(i => i.status !== 'archived');
      setIzvrsbe(filteredData);
    } catch (error) {
      toast.error("Failed to fetch izvrsbe");
    }
  }, [token, selectedVertical, searchQuery, showArchived]);

  const fetchUrgent = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/izvrsbe/alerts/urgent`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUrgentList(response.data.urgent);
    } catch (error) {
      console.error("Failed to fetch urgent alerts:", error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    fetchDashboard();
    fetchIzvrsbe();
    fetchUrgent();
  }, [fetchDashboard, fetchIzvrsbe, fetchUrgent]);

  // Handlers
  const handleCreate = async () => {
    if (!newIzvrsba.case_number || !newIzvrsba.creditor) {
      toast.error("Case number and creditor are required");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/izvrsbe/`, newIzvrsba, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Izvrsba created");
      setShowAddModal(false);
      setNewIzvrsba({
        case_number: "",
        vertical: "europacific",
        creditor: "",
        principal_amount: 0,
        amount_to_pay: 0,
        amount_paid: 0,
        costs: 0,
        comment1: "",
        comment1_date: "",
        comment2: "",
        comment2_date: "",
        notes: "",
      });
      fetchDashboard();
      fetchIzvrsbe();
      fetchUrgent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create");
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedItem || !paymentForm.amount) {
      toast.error("Payment amount is required");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/izvrsbe/${selectedItem.id}/payment`, {
        ...paymentForm,
        izvrsba_id: selectedItem.id,
        payment_date: new Date(paymentForm.payment_date).toISOString(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Payment recorded");
      setShowPaymentModal(false);
      setPaymentForm({
        amount: 0,
        payment_date: new Date().toISOString().split('T')[0],
        payment_reference: "",
        notes: "",
      });
      fetchDashboard();
      fetchIzvrsbe();
      fetchUrgent();
    } catch (error) {
      toast.error("Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedItem) return;
    
    setLoading(true);
    try {
      await axios.patch(`${API_URL}/izvrsbe/${selectedItem.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Izvrsba updated");
      setShowEditModal(false);
      setSelectedItem(null);
      fetchDashboard();
      fetchIzvrsbe();
      fetchUrgent();
    } catch (error) {
      toast.error("Failed to update izvrsba");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setEditForm({
      amount_to_pay: item.amount_to_pay || 0,
      amount_paid: item.amount_paid || 0,
      costs: item.costs || 0,
      comment1: item.comment1 || "",
      comment1_date: item.comment1_date || "",
      comment2: item.comment2 || "",
      comment2_date: item.comment2_date || "",
      status: item.status || "active",
      notes: item.notes || "",
    });
    setShowEditModal(true);
  };

  // Delete handler
  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/izvrsbe/${itemToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Izvrsba deleted permanently");
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      fetchDashboard();
      fetchIzvrsbe();
      fetchUrgent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete izvrsba");
    } finally {
      setLoading(false);
    }
  };

  // Archive handler
  const handleArchive = async () => {
    if (!itemToArchive) return;
    
    setLoading(true);
    try {
      await axios.patch(`${API_URL}/izvrsbe/${itemToArchive.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Izvrsba archived");
      setShowArchiveConfirm(false);
      setItemToArchive(null);
      fetchDashboard();
      fetchIzvrsbe();
      fetchUrgent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive izvrsba");
    } finally {
      setLoading(false);
    }
  };

  // Restore handler
  const handleRestore = async (item) => {
    setLoading(true);
    try {
      await axios.patch(`${API_URL}/izvrsbe/${item.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Izvrsba restored");
      fetchDashboard();
      fetchIzvrsbe();
      fetchUrgent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore izvrsba");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (file, vertical) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/izvrsbe/import?vertical=${vertical}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(`Imported ${response.data.imported} records`);
      if (response.data.errors?.length > 0) {
        console.warn("Import errors:", response.data.errors);
      }
      fetchDashboard();
      fetchIzvrsbe();
      fetchUrgent();
    } catch (error) {
      toast.error("Import failed");
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("sl-SI", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Get vertical name
  const getVerticalName = (id) => {
    const v = verticals.find(v => v.id === id);
    return v ? v.name : id;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 text-white px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="izvrsbe-title">
                <Scale className="h-6 w-6" />
                Izvršbe (Forced Payables)
              </h1>
              <p className="text-red-200 text-sm mt-1">
                Court-ordered payments - Critical tracking to prevent blocked accounts
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".msg,.xls,.xlsx,.csv"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleImport(e.target.files[0], selectedVertical || "europacific");
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                      asChild
                    >
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Import
                      </span>
                    </Button>
                  </label>
                  <Button
                    size="sm"
                    onClick={() => setShowAddModal(true)}
                    className="bg-white text-red-700 hover:bg-red-50"
                    data-testid="add-izvrsba-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Record
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="tab-dashboard">
              <TrendingDown className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="urgent" className="gap-2" data-testid="tab-urgent">
              <AlertOctagon className="h-4 w-4" />
              Urgent
              {urgentList.length > 0 && (
                <Badge className="ml-1 bg-red-500 text-white">{urgentList.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2" data-testid="tab-all">
              <FileText className="h-4 w-4" />
              All Records
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {dashboard && (
              <div className="space-y-6">
                {/* Alert Banner */}
                {dashboard.totals.total_urgent > 0 && (
                  <div className="bg-red-100 border border-red-300 rounded-lg p-4 flex items-center gap-4">
                    <AlertOctagon className="h-8 w-8 text-red-600" />
                    <div>
                      <h3 className="font-bold text-red-800">Urgent Attention Required!</h3>
                      <p className="text-red-700">
                        {dashboard.totals.total_urgent} izvrsbe need immediate action to prevent blocked bank accounts
                      </p>
                    </div>
                    <Button 
                      className="ml-auto bg-red-600 hover:bg-red-700"
                      onClick={() => setActiveTab("urgent")}
                    >
                      View Urgent
                    </Button>
                  </div>
                )}

                {/* Totals */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Total Cases</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{dashboard.totals.total_count}</div>
                      <p className="text-sm text-slate-500">Active court cases</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Total Principal</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-700">
                        {formatCurrency(dashboard.totals.total_principal)}
                      </div>
                      <p className="text-sm text-slate-500">Original debt</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Remaining to Pay</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-red-600">
                        {formatCurrency(dashboard.totals.total_remaining)}
                      </div>
                      <p className="text-sm text-slate-500">Outstanding balance</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Total Paid</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-green-600">
                        {formatCurrency(dashboard.totals.total_paid)}
                      </div>
                      <p className="text-sm text-slate-500">Already settled</p>
                    </CardContent>
                  </Card>
                </div>

                {/* By Vertical */}
                <Card>
                  <CardHeader>
                    <CardTitle>By Company Vertical</CardTitle>
                    <CardDescription>Breakdown of forced payables per entity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {verticals.map((vertical) => {
                        const data = dashboard.by_vertical[vertical.id];
                        if (!data) return null;
                        
                        return (
                          <div key={vertical.id} className={`p-4 rounded-lg border ${VERTICAL_COLORS[vertical.id]}`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <Building2 className="h-5 w-5" />
                                <h3 className="font-bold text-lg">{vertical.name}</h3>
                                <Badge variant="outline">{data.count} cases</Badge>
                                {data.urgent_count > 0 && (
                                  <Badge className="bg-red-500 text-white">
                                    {data.urgent_count} urgent
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold">{formatCurrency(data.total_remaining)}</div>
                                <div className="text-sm opacity-75">remaining</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="opacity-75">Principal:</span>
                                <span className="ml-2 font-medium">{formatCurrency(data.total_principal)}</span>
                              </div>
                              <div>
                                <span className="opacity-75">To Pay:</span>
                                <span className="ml-2 font-medium">{formatCurrency(data.total_to_pay)}</span>
                              </div>
                              <div>
                                <span className="opacity-75">Paid:</span>
                                <span className="ml-2 font-medium text-green-700">{formatCurrency(data.total_paid)}</span>
                              </div>
                              <div>
                                <span className="opacity-75">Costs:</span>
                                <span className="ml-2 font-medium">{formatCurrency(data.total_costs)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Urgent Tab */}
          <TabsContent value="urgent">
            <Card className="border-red-200">
              <CardHeader className="bg-red-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <AlertOctagon className="h-5 w-5" />
                      Urgent Izvršbe
                    </CardTitle>
                    <CardDescription className="text-red-600">
                      Payments requiring immediate attention - Risk of blocked bank accounts
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchUrgent} className="border-red-300">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {urgentList.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                    <p className="text-slate-500">No urgent izvrsbe at this time</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {urgentList.map((item) => (
                      <div 
                        key={item.id} 
                        className={`p-4 rounded-lg border-2 ${
                          item.urgency === 'overdue' ? 'bg-red-50 border-red-300' :
                          item.urgency === 'critical' ? 'bg-orange-50 border-orange-300' :
                          'bg-yellow-50 border-yellow-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-bold">{item.case_number}</span>
                            <Badge className={VERTICAL_COLORS[item.vertical]}>
                              {getVerticalName(item.vertical)}
                            </Badge>
                            <Badge className={URGENCY_COLORS[item.urgency]}>
                              {item.urgency === 'overdue' 
                                ? `${Math.abs(item.days_until_reminder)} days OVERDUE` 
                                : item.days_until_reminder !== null 
                                  ? `${item.days_until_reminder} days left`
                                  : 'Unknown deadline'
                              }
                            </Badge>
                          </div>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedItem(item);
                              setShowPaymentModal(true);
                            }}
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Record Payment
                          </Button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{item.creditor}</span>
                            {item.comment1 && (
                              <span className="ml-3 text-sm text-slate-500">
                                {item.comment1}
                                {item.comment1_date && <span className="text-xs ml-1">({item.comment1_date})</span>}
                              </span>
                            )}
                            {item.comment2 && (
                              <span className="ml-3 text-sm text-orange-600">
                                {item.comment2}
                                {item.comment2_date && <span className="text-xs ml-1">({item.comment2_date})</span>}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-red-600">
                              {formatCurrency(item.remaining)}
                            </div>
                            <div className="text-xs text-slate-500">remaining to pay</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Records Tab */}
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>All Izvršbe Records</CardTitle>
                    <CardDescription>Complete list of court-ordered payments</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchIzvrsbe}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search case number or creditor..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select
                    value={selectedVertical || "all"}
                    onValueChange={(v) => setSelectedVertical(v === "all" ? "" : v)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="All Verticals" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Verticals</SelectItem>
                      {verticals.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant={showArchived ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowArchived(!showArchived)}
                    className={showArchived ? "bg-slate-600 hover:bg-slate-700" : ""}
                    data-testid="toggle-archived-btn"
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    {showArchived ? "Viewing Archived" : "Show Archived"}
                  </Button>
                </div>

                {/* Table */}
                {izvrsbe.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No izvrsbe records found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="pb-2 pr-3">Case #</th>
                          <th className="pb-2 pr-3">Vertical</th>
                          <th className="pb-2 pr-3">Creditor</th>
                          <th className="pb-2 pr-3 text-right">Principal</th>
                          <th className="pb-2 pr-3 text-right">To Pay</th>
                          <th className="pb-2 pr-3 text-right">Paid</th>
                          <th className="pb-2 pr-3 text-right">Remaining</th>
                          <th className="pb-2 pr-3">Comment 1</th>
                          <th className="pb-2 pr-3">Comment 2</th>
                          <th className="pb-2 pr-3">Status</th>
                          <th className="pb-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {izvrsbe.map((item) => (
                          <tr key={item.id} className={`border-b hover:bg-slate-50 ${
                            item.urgency === 'overdue' ? 'bg-red-50' :
                            item.urgency === 'critical' ? 'bg-orange-50' : ''
                          }`}>
                            <td className="py-2 pr-3 font-mono">{item.case_number}</td>
                            <td className="py-2 pr-3">
                              <Badge className={VERTICAL_COLORS[item.vertical]} variant="outline">
                                {getVerticalName(item.vertical)}
                              </Badge>
                            </td>
                            <td className="py-2 pr-3 font-medium">{item.creditor}</td>
                            <td className="py-2 pr-3 text-right">{formatCurrency(item.principal_amount)}</td>
                            <td className="py-2 pr-3 text-right">{formatCurrency(item.amount_to_pay)}</td>
                            <td className="py-2 pr-3 text-right text-green-600">{formatCurrency(item.amount_paid)}</td>
                            <td className="py-2 pr-3 text-right font-bold text-red-600">
                              {formatCurrency(item.remaining)}
                            </td>
                            <td className="py-2 pr-3 max-w-[200px]">
                              {item.comment1 ? (
                                <div className="text-slate-700">
                                  <span className="font-medium">{item.comment1}</span>
                                  {item.comment1_date && (
                                    <span className="text-xs text-slate-500 ml-1">({item.comment1_date})</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-2 pr-3 max-w-[200px]">
                              {item.comment2 ? (
                                <div className="text-orange-600">
                                  <span className="font-medium">{item.comment2}</span>
                                  {item.comment2_date && (
                                    <span className="text-xs text-orange-500 ml-1">({item.comment2_date})</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-2 pr-3">
                              <Badge className={URGENCY_COLORS[item.urgency]}>
                                {item.urgency}
                              </Badge>
                            </td>
                            <td className="py-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={`actions-btn-${item.id}`}>
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditModal(item)} data-testid={`edit-btn-${item.id}`}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    setSelectedItem(item);
                                    setShowPaymentModal(true);
                                  }} data-testid={`payment-btn-${item.id}`}>
                                    <CreditCard className="h-4 w-4 mr-2" />
                                    Record Payment
                                  </DropdownMenuItem>
                                  {item.status === 'archived' ? (
                                    <DropdownMenuItem 
                                      onClick={() => handleRestore(item)}
                                      className="text-green-600"
                                      data-testid={`restore-btn-${item.id}`}
                                    >
                                      <RotateCcw className="h-4 w-4 mr-2" />
                                      Restore
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setItemToArchive(item);
                                        setShowArchiveConfirm(true);
                                      }}
                                      className="text-orange-600"
                                      data-testid={`archive-btn-${item.id}`}
                                    >
                                      <Archive className="h-4 w-4 mr-2" />
                                      Archive
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setItemToDelete(item);
                                      setShowDeleteConfirm(true);
                                    }}
                                    className="text-red-600"
                                    data-testid={`delete-btn-${item.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Izvrsba Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Izvrsba Record</DialogTitle>
            <DialogDescription>Enter forced payment details</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Case Number *</Label>
                <Input
                  value={newIzvrsba.case_number}
                  onChange={(e) => setNewIzvrsba(f => ({ ...f, case_number: e.target.value }))}
                  placeholder="VL12345/2026"
                />
              </div>
              <div>
                <Label>Vertical *</Label>
                <Select
                  value={newIzvrsba.vertical}
                  onValueChange={(v) => setNewIzvrsba(f => ({ ...f, vertical: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {verticals.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Creditor (Upnik) *</Label>
              <Input
                value={newIzvrsba.creditor}
                onChange={(e) => setNewIzvrsba(f => ({ ...f, creditor: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Principal Amount (Glavnica)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newIzvrsba.principal_amount}
                  onChange={(e) => setNewIzvrsba(f => ({ ...f, principal_amount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Amount To Pay (Za Plačat)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newIzvrsba.amount_to_pay}
                  onChange={(e) => setNewIzvrsba(f => ({ ...f, amount_to_pay: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount Paid (Plačano)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newIzvrsba.amount_paid}
                  onChange={(e) => setNewIzvrsba(f => ({ ...f, amount_paid: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Costs (Stroški)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newIzvrsba.costs}
                  onChange={(e) => setNewIzvrsba(f => ({ ...f, costs: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Comment 1</Label>
                <div className="flex gap-2">
                  <Input
                    value={newIzvrsba.comment1}
                    onChange={(e) => setNewIzvrsba(f => ({ ...f, comment1: e.target.value }))}
                    placeholder="e.g., OBRESTI"
                    className="flex-1"
                  />
                  <Input
                    type="date"
                    value={newIzvrsba.comment1_date}
                    onChange={(e) => setNewIzvrsba(f => ({ ...f, comment1_date: e.target.value }))}
                    className="w-36"
                  />
                </div>
              </div>
              <div>
                <Label>Comment 2 / Deadline</Label>
                <div className="flex gap-2">
                  <Input
                    value={newIzvrsba.comment2}
                    onChange={(e) => setNewIzvrsba(f => ({ ...f, comment2: e.target.value }))}
                    placeholder="e.g., PLAČAT DO 19.02.2026"
                    className="flex-1"
                  />
                  <Input
                    type="date"
                    value={newIzvrsba.comment2_date}
                    onChange={(e) => setNewIzvrsba(f => ({ ...f, comment2_date: e.target.value }))}
                    className="w-36"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={newIzvrsba.notes}
                onChange={(e) => setNewIzvrsba(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={loading} className="bg-red-600 hover:bg-red-700">
              {loading ? "Creating..." : "Create Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {selectedItem && (
                <span>Recording payment for {selectedItem.case_number} - {selectedItem.creditor}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-slate-500">Remaining to pay:</span>
                  <span className="font-bold text-red-600">{formatCurrency(selectedItem.remaining)}</span>
                </div>
              </div>

              <div>
                <Label>Payment Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
                />
              </div>

              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))}
                />
              </div>

              <div>
                <Label>Payment Reference</Label>
                <Input
                  value={paymentForm.payment_reference}
                  onChange={(e) => setPaymentForm(f => ({ ...f, payment_reference: e.target.value }))}
                  placeholder="Bank reference number"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? "Recording..." : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Izvrsba Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Izvrsba</DialogTitle>
            <DialogDescription>
              {selectedItem && (
                <span className="font-mono font-bold">{selectedItem.case_number}</span>
              )} - {selectedItem?.creditor}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount to Pay (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.amount_to_pay}
                  onChange={(e) => setEditForm(f => ({ ...f, amount_to_pay: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Amount Paid (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.amount_paid}
                  onChange={(e) => setEditForm(f => ({ ...f, amount_paid: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Costs (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.costs}
                  onChange={(e) => setEditForm(f => ({ ...f, costs: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="settled">Settled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Comment 1</Label>
                <Input
                  value={editForm.comment1}
                  onChange={(e) => setEditForm(f => ({ ...f, comment1: e.target.value }))}
                  placeholder="e.g., OBRESTI (interest)"
                />
              </div>
              <div>
                <Label>Comment 1 Date</Label>
                <Input
                  value={editForm.comment1_date}
                  onChange={(e) => setEditForm(f => ({ ...f, comment1_date: e.target.value }))}
                  placeholder="DD.MM.YYYY"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Comment 2 (Payment Deadline)</Label>
                <Input
                  value={editForm.comment2}
                  onChange={(e) => setEditForm(f => ({ ...f, comment2: e.target.value }))}
                  placeholder="e.g., PLAČAT DO 19.02.2026"
                />
              </div>
              <div>
                <Label>Comment 2 Date</Label>
                <Input
                  value={editForm.comment2_date}
                  onChange={(e) => setEditForm(f => ({ ...f, comment2_date: e.target.value }))}
                  placeholder="DD.MM.YYYY"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                rows={3}
                placeholder="Additional notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Izvrsba
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this record?
            </DialogDescription>
          </DialogHeader>

          {itemToDelete && (
            <div className="py-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="font-mono font-bold text-lg">{itemToDelete.case_number}</div>
                <div className="text-slate-600">{itemToDelete.creditor}</div>
                <div className="mt-2 text-sm text-red-600">
                  <strong>Warning:</strong> This action cannot be undone. All payment history will also be deleted.
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setItemToDelete(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              disabled={loading} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {loading ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Archive className="h-5 w-5" />
              Archive Izvrsba
            </DialogTitle>
            <DialogDescription>
              Archive this record to hide it from the active list. You can restore it later.
            </DialogDescription>
          </DialogHeader>

          {itemToArchive && (
            <div className="py-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="font-mono font-bold text-lg">{itemToArchive.case_number}</div>
                <div className="text-slate-600">{itemToArchive.creditor}</div>
                <div className="mt-2 text-sm text-orange-700">
                  Remaining: <strong>{formatCurrency(itemToArchive.remaining)}</strong>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowArchiveConfirm(false);
              setItemToArchive(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleArchive} 
              disabled={loading} 
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-archive-btn"
            >
              {loading ? "Archiving..." : "Archive Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
